import { getArgumentsFromStoreFieldName } from "@/cache/Utils";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client/react";
import {
  BidDecisionFragmentDoc,
  FetchTenderByIdDocument,
  MarginDecisionFragmentDoc,
  CommentOnDecisionGroupMutation,
  CommentOnDecisionGroupMutationVariables,
  CommentOnDecisionGroupDocument,
} from "@/graphql/__generated__/graphql";
import { Reference, StoreObject } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CustomToastComponent } from "@/components/shared";

/**
 * Custom hook for commenting on a decision group and handling the necessary cache updates.
 * @returns {Object} - Contains the submitDecisionMutation function, loading state, and error state.
 */
export default function useCommentOnDecisionGroupMutationHook(): { commentOnDecisionGroupMutation: any, loading: any, error: any } {
  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const [commentOnDecisionGroupMutation, { loading, error }] = useMutation<CommentOnDecisionGroupMutation, CommentOnDecisionGroupMutationVariables>(
    CommentOnDecisionGroupDocument, {
      onCompleted: () => {
        toast(CustomToastComponent, {
          toastId: "comment-decision-group-success",
          data: {
            title: t("toasts.commentondecisiongroup"),
            type: "success"
          }
        });
      },
      refetchQueries: [
        { query: FetchTenderByIdDocument, variables: { id: Number(tenderID) } }
      ],
      update: (cache, { data }) => {
        if (!data?.commentOnDecisionGroup) {
          return;
        }

        const { marginDecision, bidDecision } = data.commentOnDecisionGroup;

        /**
       * Update the cache for the 'fetchMarginDecisionsByGroup' query.
       */
        if (marginDecision) {
          cache.modify({
            fields: {
              fetchMarginDecisionsByGroup(existingData = {}, { storeFieldName, readField }) {
              // Extract arguments from 'storeFieldName'
                const args = getArgumentsFromStoreFieldName(storeFieldName);

                // If arguments are missing or don't match, return existing data
                if (!args || args.marginDecisionGroupId !== marginDecision.marginDecisionGroupId) {
                  return existingData;
                }

                const existingMarginDecisions = existingData.marginDecisions || [];

                // Check for duplicates
                if (
                  existingMarginDecisions.some(
                    (decisionRef: Reference | StoreObject | undefined) => readField("id", decisionRef) === marginDecision.id
                  )
                ) {
                  return existingData;
                }

                // Write the new margin decision to the cache
                const newMarginDecisionRef = cache.writeFragment({
                  data: marginDecision,
                  fragment: MarginDecisionFragmentDoc,
                });

                // Return the updated data with the new margin decision added
                return {
                  ...existingData,
                  marginDecisions: [...existingMarginDecisions, newMarginDecisionRef]
                };
              }
            }
          });
        }

        /**
       * Update the cache for the 'fetchBidDecisionsByGroup' query.
       */
        if (bidDecision) {
          cache.modify({
            fields: {
              fetchBidDecisionsByGroup(existingData = {}, { storeFieldName, readField }) {
              // Extract arguments from 'storeFieldName'
                const args = getArgumentsFromStoreFieldName(storeFieldName);

                // If arguments are missing or don't match, return existing data
                if (!args || args.bidDecisionGroupId !== bidDecision.bidDecisionGroupId) {
                  return existingData;
                }

                const existingBidDecisions = existingData.bidDecisions || [];

                // Check for duplicates
                if (
                  existingBidDecisions.some(
                    (decisionRef: Reference | StoreObject | undefined) => readField("id", decisionRef) === bidDecision.id
                  )
                ) {
                  return existingData;
                }

                // Write the new bid decision to the cache
                const newBidDecisionRef = cache.writeFragment({
                  data: bidDecision,
                  fragment: BidDecisionFragmentDoc,
                });

                // Return the updated data with the new bid decision added
                return {
                  ...existingData,
                  bidDecisions: [...existingBidDecisions, newBidDecisionRef]
                };
              }
            }
          });
        }
      }
    });

  return {
    commentOnDecisionGroupMutation,
    loading,
    error
  };
}
